import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import { FaPaperPlane, FaRobot, FaUser } from "react-icons/fa";
import Spinner from "react-bootstrap/Spinner";
import ReactMarkdown from "react-markdown";
import { AIChatbot } from "../../../features/ai_chatbots/aiChatbotsService";
import apiClient from "../../../api/apiClient";
import { useTranslation } from "react-i18next";
import { Icon } from "../../../components/common/Icon";

interface AIChatbotTestModalProps {
  show: boolean;
  onHide: () => void;
  chatbot: AIChatbot | null;
}

interface Message {
  sender: "user" | "bot";
  text: string;
  timestamp: Date;
}

const AIChatbotTestModal: React.FC<AIChatbotTestModalProps> = ({
  show,
  onHide,
  chatbot,
}) => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFlowActive, setIsFlowActive] = useState(false);
  const [sessionId, setSessionId] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  // Kaydırma için efekt
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Modal her açıldığında veya chatbot değiştiğinde resetle
  useEffect(() => {
    if (show && chatbot) {
      const widgetKey = chatbot.widget_key || chatbot.id;
      setSessionId("test_" + String(widgetKey));
      setIsFlowActive(false);

      if (chatbot.initial_message) {
        setMessages([
          {
            sender: "bot",
            text: t(chatbot.initial_message),
            timestamp: new Date(),
          },
        ]);
      } else {
        setMessages([]);
      }
    }
  }, [show, chatbot?.id]);

  const handleCancelFlow = async () => {
    if (!chatbot || !sessionId) return;
    try {
      const widgetKey = chatbot.widget_key || chatbot.id;
      await apiClient.post(`/public/chatbot/${widgetKey}/cancel-flow`, {
        session_id: sessionId
      });

      setIsFlowActive(false);

      const cancelMsg: Message = {
        sender: "bot",
        text: t("flows.flow_ended_message", "Flow ended. How else can I help you?"),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, cancelMsg]);

    } catch (error) {
      console.error("Error cancelling flow:", error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || !chatbot) return;

    // Kullanıcı mesajını ekle
    const userMessage: Message = {
      sender: "user",
      text: message,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setMessage("");
    setIsLoading(true);

    try {
      const widgetKey = chatbot.widget_key || chatbot.id;

      const response = await apiClient.post(`/public/chatbot/${widgetKey}/chat`, {
        message: message.trim(),
        mode: "test",
        session_id: sessionId
      });

      const data = (response as any).data || (response as any);

      if (data.session_id) {
        setSessionId(data.session_id);
      }

      setIsFlowActive(!!data.is_flow_active);

      const botMessage: Message = {
        sender: "bot",
        text:
          data?.message || (response as any).data?.message || (response as any)?.message || t("not_response_for_ai_chatbot"),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
      // Hata mesajı ekle
      const errorMessage: Message = {
        sender: "bot",
        text: t("error_sending_message"),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleClose = () => {
    setMessages([]);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      className="chatbot-test-modal"
    >
      <Modal.Header className="chatbot-modal-header">
        <div className="d-flex align-items-center gap-3">
          <div className="chatbot-avatar">
            <Icon icon={FaRobot} />
          </div>
          <div>
            <Modal.Title className="chatbot-modal-title">
              {chatbot?.name}
            </Modal.Title>
            <small className="text-muted">Test Mode - AI Assistant</small>
          </div>
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleClose}
          className="rounded-circle p-2"
        >
          ×
        </Button>
      </Modal.Header>

      <Modal.Body className="p-0 chatbot-modal-body">
        <div className="chat-container">
          {messages.length === 0 ? (
            <div className="empty-chat-state">
              <div className="empty-chat-icon">
                <Icon icon={FaRobot} />
              </div>
              <h6 className="empty-chat-title">Sohbeti Başlatın</h6>
              <p className="empty-chat-text">
                AI asistanınızla konuşmaya başlamak için aşağıdan bir mesaj
                gönderin.
              </p>
            </div>
          ) : (
            <div className="messages-container">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`message-wrapper ${msg.sender === "user" ? "user-message" : "bot-message"}`}
                >
                  <div className="message-avatar">
                    <Icon icon={msg.sender === "user" ? FaUser : FaRobot} />
                  </div>
                  <div className="message-content">
                    <div className="message-bubble">
                      <div className="message-text">
                        {msg.sender === "bot" ? (
                          <ReactMarkdown>{msg.text}</ReactMarkdown>
                        ) : (
                          msg.text
                        )}
                      </div>
                      <div className="message-time">
                        {formatTime(msg.timestamp)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="message-wrapper bot-message">
                  <div className="message-avatar">
                    <Icon icon={FaRobot} />
                  </div>
                  <div className="message-content">
                    <div className="message-bubble typing-indicator">
                      <div className="typing-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="chatbot-modal-footer d-flex flex-column">
        {isFlowActive && (
          <div className="w-100 mb-2 d-flex justify-content-center">
            <Button variant="outline-secondary" size="sm" onClick={handleCancelFlow} className="flow-end-btn">
              {t("flows.end_flow_button", "End flow")}
            </Button>
          </div>
        )}
        <Form className="w-100" onSubmit={handleSendMessage}>
          <InputGroup className="message-input-group">
            <Form.Control
              className="message-input"
              placeholder={
                t("write_your_message") || "Mesajınızı yazın..."
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isLoading || !chatbot}
            />
            <Button
              variant="primary"
              type="submit"
              disabled={isLoading || !message.trim() || !chatbot}
              className="send-button"
            >
              <Icon icon={FaPaperPlane} />
            </Button>
          </InputGroup>
        </Form>
      </Modal.Footer>


    </Modal>
  );
};

export default AIChatbotTestModal;
